import { useEffect, useState, useRef } from "react";
import ApexCharts from 'react-apexcharts';
import { DatePicker, Modal, Button, Form, Input, Tooltip } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { spotPriceGraphData, spotPricePost } from "../../features/NewsRoom/NewsRoomSlice";
import { toggleModal, updateModalInfo } from "../../features/App/AppSlice";
import { InfoCircleOutlined } from "@ant-design/icons";


const CarbonPricingChart = () => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.user);
  const isAdmin = user?.is_admin === true || user?.role === "admin";

  const [xData, setXData] = useState([]);
  const [graphData, setGraphData] = useState([]);
  const [activeTime, setActiveTime] = useState('ALL');
  const [form] = Form.useForm();
  const [modalVisible, setModalVisible] = useState(false);
  const [recentRows, setRecentRows] = useState([]);

  // Insights API state
  const [currentSpotPrice, setCurrentSpotPrice] = useState(null);
  const [previousDayClose, setPreviousDayClose] = useState(null);
  const [insightsWeekHigh, setInsightsWeekHigh] = useState(null);
  const [insightsWeekLow, setInsightsWeekLow] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [ukEtsPrice, setUkEtsPrice] = useState(null);
  const [ukEtsPreviousClose, setUkEtsPreviousClose] = useState(null);

  const getspotPriceData = () => {
    dispatch(spotPriceGraphData(activeTime)).then((result) => {
      const payload = result?.payload;
      if (!payload) { setXData([]); setGraphData([]); return; }
      const records = Array.isArray(payload) ? payload : Array.isArray(payload.graph) ? payload.graph : [];
      if (records.length === 0) { setXData([]); setGraphData([]); return; }
      const xVals = records.map(item => new Date(item.date).getTime());
      const yVals = records.map(item => parseFloat(item.price) || 0);
      setXData(xVals);
      setGraphData(yVals);
    });
  };

  useEffect(() => { getspotPriceData(); }, [activeTime]);

  const fetchSpotInsights = async () => {
    setInsightsLoading(true);
    try {
      const response = await fetch('https://be.azolla.sg/api/get_spot_insights/', { method: 'GET', redirect: 'follow' });
      const result = await response.json();
      if (result.success) {
        setCurrentSpotPrice(result.current_spot_price);
        setPreviousDayClose(result.previous_day_close);
        setInsightsWeekHigh(result.week_high);
        setInsightsWeekLow(result.week_low);
        setUkEtsPrice(result.uk_ets_price ?? null);
        setUkEtsPreviousClose(result.uk_ets_previous_day_close ?? null);
        if (result.history?.records?.length > 0) {
          const rows = result.history.records.map((rec) => ({
            date:  new Date(rec.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
            open:  rec.market_open_price  != null ? parseFloat(rec.market_open_price).toFixed(2)  : '—',
            high:  rec.market_high_price  != null ? parseFloat(rec.market_high_price).toFixed(2)  : '—',
            low:   rec.market_low_price   != null ? parseFloat(rec.market_low_price).toFixed(2)   : '—',
            close: rec.market_close_price != null ? parseFloat(rec.market_close_price).toFixed(2) : '—',
            spot:  rec.spot_price         != null ? parseFloat(rec.spot_price).toFixed(2)         : '—',
          }));
          setRecentRows(rows);
        }
      }
    } catch (e) {
      console.error('Failed to fetch spot insights:', e);
    } finally {
      setInsightsLoading(false);
    }
  };

  useEffect(() => { fetchSpotInsights(); }, []);

  const getFilteredData = () => {
    if (!xData.length) return { x: [], y: [] };
    if (activeTime === 'ALL') return { x: xData, y: graphData };
    const daysMap = { 'INTRADAY': 1, '1W': 7, '1M': 30, '3M': 90 };
    const daysBack = daysMap[activeTime];
    if (!daysBack) return { x: xData, y: graphData };
    const now = new Date();
    now.setHours(23, 59, 59, 999);
    const cutoff = new Date(now);
    cutoff.setDate(now.getDate() - daysBack);
    const cutoffTime = cutoff.getTime();
    const fx = [], fy = [];
    xData.forEach((x, i) => { if (x >= cutoffTime) { fx.push(x); fy.push(graphData[i]); } });
    return { x: fx, y: fy };
  };

  const { x: filteredX, y: filteredY } = getFilteredData();

  const chartOptions = {
    series: [{ name: 'EUA Spot Price', data: filteredY }],
    chart: { type: 'area', toolbar: { show: true, tools: { download: true, selection: true, zoom: true, zoomin: false, zoomout: false, pan: false, reset: false } }, zoom: { enabled: true, type: 'x', autoScaleYaxis: true }, selection: { enabled: true, type: 'x' }, background: 'transparent', animations: { enabled: false } },
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: 2 },
    xaxis: {
      type: 'datetime',
      categories: filteredX,
      labels: { style: { colors: '#000000', fontSize: '11px' }, datetimeFormatter: { month: "MMM 'yy" } },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: { colors: '#000000', fontSize: '11px' },
        formatter: (v) => `€${parseFloat(v).toFixed(activeTime === 'INTRADAY' ? 2 : 0)}`,
      },
      ...(activeTime === 'INTRADAY' && filteredY.length > 0 ? (() => {
        const minY = Math.min(...filteredY);
        const maxY = Math.max(...filteredY);
        const pad  = (maxY - minY) * 0.1 || 0.05;
        return { min: parseFloat((minY - pad).toFixed(2)), max: parseFloat((maxY + pad).toFixed(2)), tickAmount: 5 };
      })() : {}),
    },
    grid: { borderColor: '#f1f5f9' },
    tooltip: { x: { format: 'dd/MM/yy' }, y: { formatter: (v) => `€${parseFloat(v).toFixed(2)} EUR/tCO₂` }, theme: 'dark' },
    fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.15, opacityTo: 0.02, stops: [0, 100] } },
    colors: ['#0a8f7a'],
    legend: { show: false },
  };

  const [dateSelected, setDateSelected] = useState(false);
  const [fetchingSpotPrice, setFetchingSpotPrice] = useState(false);
  const chartRef = useRef(null);
  const [zoomLevel, setZoomLevel] = useState(0);

  const openModal = () => { form.resetFields(); setDateSelected(false); setModalVisible(true); };

  const handleZoomIn = () => {
    if (!chartRef.current || !filteredX.length) return;
    const chart = chartRef.current.chart;
    const minT = Math.min(...filteredX);
    const maxT = Math.max(...filteredX);
    const range = maxT - minT;
    const newLevel = zoomLevel + 1;
    const factor = Math.max(1 - newLevel * 0.2, 0.1);
    const center = minT + range / 2;
    chart.zoomX(center - (range * factor) / 2, center + (range * factor) / 2);
    setZoomLevel(newLevel);
  };

  const handleZoomOut = () => {
    if (!chartRef.current || !filteredX.length) return;
    const chart = chartRef.current.chart;
    const minT = Math.min(...filteredX);
    const maxT = Math.max(...filteredX);
    const range = maxT - minT;
    const newLevel = Math.max(zoomLevel - 1, 0);
    const factor = Math.max(1 - newLevel * 0.2, 0.1);
    const center = minT + range / 2;
    chart.zoomX(center - (range * factor) / 2, center + (range * factor) / 2);
    setZoomLevel(newLevel);
  };

  const handleResetZoom = () => {
    setActiveTime('ALL');
    setZoomLevel(0);
    if (chartRef.current) chartRef.current.chart.resetSeries();
  };

  const handleDateChange = async (date) => {
    if (!date) {
      setDateSelected(false);
      form.setFieldsValue({ openingPrice: '', highestPrice: '', lowestPrice: '', closingPrice: '' });
      return;
    }
    const dateStr = date.format('YYYY-MM-DD');
    setFetchingSpotPrice(true);
    try {
      const response = await fetch(`https://be.azolla.sg/api/get_spot_price/?date=${dateStr}`, { method: 'GET' });
      const result = await response.json();
      if (result.success) {
        form.setFieldsValue({
          openingPrice: String(result.market_open),
          highestPrice: String(result.highest),
          lowestPrice:  String(result.lowest),
          closingPrice: String(result.market_close),
        });
      } else {
        form.setFieldsValue({ openingPrice: '', highestPrice: '', lowestPrice: '', closingPrice: '' });
      }
      setDateSelected(true);
    } catch (e) {
      console.error(e);
      form.setFieldsValue({ openingPrice: '', highestPrice: '', lowestPrice: '', closingPrice: '' });
      setDateSelected(true);
    } finally {
      setFetchingSpotPrice(false);
    }
  };

  const handleFormSubmit = (values) => {
    const date = values.date.format('YYYY-MM-DD');
    dispatch(spotPricePost({
      spot_price:    parseFloat(values.closingPrice),
      date,
      opening_price: parseFloat(values.openingPrice),
      highest_price: parseFloat(values.highestPrice),
      lowest_price:  parseFloat(values.lowestPrice),
    })).then((result) => {
      getspotPriceData();
      fetchSpotInsights();
      dispatch(toggleModal());
      dispatch(updateModalInfo({
        show: true,
        result:  result.payload ? 'Spot Price Added Successfully!' : 'Spot price Update Failed!',
        type:    result.payload ? 'success' : 'error',
        message: result.payload ? 'Success!' : 'Failed',
      }));
    }).catch((error) => dispatch(updateModalInfo({ show: true, result: error.toString(), type: 'error', message: 'Failed' })));
    setModalVisible(false);
  };

  // EUA day-over-day change
  const euaDayChange = currentSpotPrice != null && previousDayClose != null && previousDayClose !== 0
    ? ((currentSpotPrice - previousDayClose) / previousDayClose * 100) : null;
  const euaBadge     = euaDayChange != null ? `${euaDayChange >= 0 ? '↑' : '↓'} ${Math.abs(euaDayChange).toFixed(1)}% DoD` : '—';
  const euaBadgeType = euaDayChange != null ? (euaDayChange >= 0 ? 'positive' : 'negative') : 'neutral';

  // UKA day-over-day change
  const ukaDayChange = ukEtsPrice != null && ukEtsPreviousClose != null && ukEtsPreviousClose !== 0
    ? ((ukEtsPrice - ukEtsPreviousClose) / ukEtsPreviousClose * 100) : null;
  const ukaBadge     = ukaDayChange != null ? `${ukaDayChange >= 0 ? '↑' : '↓'} ${Math.abs(ukaDayChange).toFixed(1)}% DoD` : '—';
  const ukaBadgeType = ukaDayChange != null ? (ukaDayChange >= 0 ? 'positive' : 'negative') : 'neutral';

  // Week range badge for EUA Last Close card
  const weekRangeBadge = insightsWeekHigh != null && insightsWeekLow != null
    ? `€${(insightsWeekHigh - insightsWeekLow).toFixed(1)} range` : '—';

  // symbol stored separately so it can be rendered green independently
  const statCards = [
    { label: 'EUA SPOT',       symbol: '€', value: currentSpotPrice   != null ? parseFloat(currentSpotPrice).toFixed(2)   : null, },
    { label: 'EUA LAST CLOSE', symbol: '€', value: previousDayClose   != null ? parseFloat(previousDayClose).toFixed(2)   : null,  },
    { label: 'UKA SPOT',       symbol: '£', value: ukEtsPrice         != null ? parseFloat(ukEtsPrice).toFixed(2)         : null,  },
    { label: 'UKA LAST CLOSE', symbol: '£', value: ukEtsPreviousClose != null ? parseFloat(ukEtsPreviousClose).toFixed(2) : null,  },
  ];

  return (
    <div className="nm-market-root">
      <div className="nm-top-row">

        {/* ── Stat Cards ── */}
        <div className="nm-stats-grid-4">
          {statCards.map((card, i) => (
            <div className="nm-stat-card" key={i}>
              <div className="nm-stat-header">
                <span className="nm-stat-label" style={{ display: 'flex', alignItems: 'center' }}>
                  {card.label}
                  {card.label === 'EUA SPOT' && <span className="nm-live-dot" />}
                </span>
              </div>
              <div className="nm-stat-value">
                {card.value != null
                  ? <><span style={{ color: 'black' }}>{card.symbol}</span>{card.value}</>
                  : '—'
                }
                {card.unit && <span className="nm-stat-unit">{card.unit}</span>}
              </div>
            </div>
          ))}
        </div>

        {/* ── Market Metrics ── */}
        <div className="nm-sidebarr-section">
          <h2 className="nm-section-title"> EUA Market Metrics</h2>
          <div className="nm-metric-grid">
            <div className="nm-metric-box">
              <div className="nm-metric-label">Week High</div>
              <div className="nm-metric-value">
                {insightsWeekHigh != null
                  ? <><span>€</span>{parseFloat(insightsWeekHigh).toFixed(2)}</>
                  : '—'}
              </div>
            </div>
            <div className="nm-metric-box">
              <div className="nm-metric-label">Week Low</div>
              <div className="nm-metric-value">
                {insightsWeekLow != null
                  ? <><span>€</span>{parseFloat(insightsWeekLow).toFixed(2)}</>
                  : '—'}
              </div>
            </div>
          </div>
        </div>

        {/* ── Chart ── */}
      <div className="nm-chart-section">
  <div className="nm-chart-header">
    <h2 className="nm-chart-title">Price Movement <Tooltip
          title={
            <span className="maptip">
              EUA market hours: 08:00–18:00 CET / 09:00–19:00 CEST. Prices displayed on the graph reflect trading activity during market hours.
            </span>
          }
          placement="top"
          getPopupContainer={() => document.body}
          overlayInnerStyle={{ whiteSpace: "normal", maxWidth: 260 }}
        >
          <InfoCircleOutlined style={{ color: "#8c8c8c", cursor: "pointer" }} />
        </Tooltip></h2>
             
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div className="nm-time-selector">
                {['INTRADAY', '1W', '1M', '3M', 'ALL'].map((t) => (
                  <button key={t} className={`nm-time-btn ${activeTime === t ? 'active' : ''}`} onClick={() => setActiveTime(t)}>
                    {t}
                  </button>
                ))}
              </div>
              {/* Custom zoom controls: +  −  ⌂ */}
              <div className="nm-zoom-controls">
                <button className="nm-zoom-btn" onClick={handleZoomIn} title="Zoom In">＋</button>
                <button className="nm-zoom-btn" onClick={handleZoomOut} title="Zoom Out">－</button>
                <button className="nm-zoom-btn nm-zoom-btn--home" onClick={handleResetZoom} title="Reset to All">⌂</button>
              </div>
              {isAdmin && (
                <span className="genericButton" onClick={openModal}>+ Spot Price</span>
              )}
            </div>
          </div>
          <div className="nm-chart-canvas-wrapper">
            <ApexCharts ref={chartRef} options={chartOptions} series={chartOptions.series} type="area" width="100%" height="100%" />
          </div>
        </div>

        {/* ── Recent Trading Activity ── */}
        <div className="nm-table-section">
          <h2 className="nm-section-title">Recent Trading Activity</h2>
          <table className="nm-data-table">
            <thead>
              <tr>
                <th style={{ color: '#0d3d5c' }}>Date</th>
                <th>Open</th><th>High</th><th>Low</th><th>Close</th>
              </tr>
            </thead>
            <tbody>
              {recentRows.length > 0 ? recentRows.map((row, i) => (
                <tr key={i}>
                  <td style={{ color: '#0d3d5c' }}>{row.date}</td>
                  <td>{row.open  !== '—' ? `€${row.open}`  : '—'}</td>
                  <td>{row.high  !== '—' ? `€${row.high}`  : '—'}</td>
                  <td>{row.low   !== '—' ? `€${row.low}`   : '—'}</td>
                  <td>{row.close !== '—' ? `€${row.close}` : '—'}</td>
                </tr>
              )) : (
                <tr><td colSpan={6} style={{ textAlign: 'center', color: '#94a3b8', padding: '1.5rem' }}>No data available</td></tr>
              )}
            </tbody>
          </table>
        </div>

      </div>

      {/* ── Spot Price Modal ── */}
      <Modal title="Spot Price Details" open={modalVisible} onCancel={() => setModalVisible(false)} footer={null} className="spotPriceModal">
        <Form layout="vertical" className="spotPriceForm" form={form} onFinish={handleFormSubmit}>
          <div className="spotPriceInputs">
            <Form.Item name="date" label="Date" rules={[{ required: true, message: 'Please select a date' }]}>
              <DatePicker style={{ width: '100%' }} onChange={handleDateChange} disabled={fetchingSpotPrice} />
            </Form.Item>
            <Form.Item name="openingPrice" label="Opening Price" rules={[{ required: true, message: 'Please input opening price' }]}>
              <Input placeholder="Opening Price" disabled={!dateSelected || fetchingSpotPrice} onKeyDown={(e) => { if (!/^[0-9.]$/.test(e.key) && e.key !== 'Backspace') e.preventDefault(); }} />
            </Form.Item>
          </div>
          <div className="spotPriceInputs">
            <Form.Item name="highestPrice" label="Highest Price" rules={[{ required: true, message: 'Please input highest price' }]}>
              <Input placeholder="Highest Price" disabled={!dateSelected || fetchingSpotPrice} onKeyDown={(e) => { if (!/^[0-9.]$/.test(e.key) && e.key !== 'Backspace') e.preventDefault(); }} />
            </Form.Item>
            <Form.Item name="lowestPrice" label="Lowest Price" rules={[{ required: true, message: 'Please input lowest price' }]}>
              <Input placeholder="Lowest Price" disabled={!dateSelected || fetchingSpotPrice} onKeyDown={(e) => { if (!/^[0-9.]$/.test(e.key) && e.key !== 'Backspace') e.preventDefault(); }} />
            </Form.Item>
          </div>
          <div style={{ maxWidth: '48%' }}>
            <Form.Item name="closingPrice" label="Closing Price" rules={[{ required: true, message: 'Please input closing price' }]}>
              <Input placeholder="Closing Price" disabled={!dateSelected || fetchingSpotPrice} onKeyDown={(e) => { if (!/^[0-9.]$/.test(e.key) && e.key !== 'Backspace') e.preventDefault(); }} />
            </Form.Item>
          </div>
          <Form.Item className="formButtonSpot">
            <Button type="primary" htmlType="submit" loading={fetchingSpotPrice}>Submit</Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CarbonPricingChart;
